"use client";

import React from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { ChevronRight, Quote, Home, Phone, Mail } from 'lucide-react';
import Image from 'next/image';
import { motion, Variants } from 'framer-motion';
import HeaderBefore from "@/app/components/headerBefore";
import HeaderAfter from "@/app/components/headerAfter";
import FooterBefore from '@/app/components/footerBefore';
import FooterAfter from '@/app/components/footerAfter';
import LoadingSpinner from '@/app/components/loadingSpinner';
import Breadcrumb from '@/app/components/breadcrumb';

export default function AboutUsPage() {
  const { status } = useSession();
  const homePath = status === 'authenticated' ? '/dashboard' : '/';

  const textAnimation: Variants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: "easeInOut",
      },
    },
  };

  if (status === 'loading') {
    return <LoadingSpinner />;
  }

  return (
    <>
      {status === 'authenticated' ? <HeaderAfter /> : <HeaderBefore />}

      {/* Hero Section */}
      <section className="relative w-full h-[400px] md:h-[550px] overflow-hidden">
        <motion.div
          className="absolute inset-0 w-full h-full overflow-hidden"
          initial={{ scale: 1.1, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <Image
            src='/mainAboutus.png'
            alt="An image representing the digital transformation journey in Sarawak."
            fill
            className="object-cover object-[55%_35%] md:object-[50%_35%]"
            priority
          />
        </motion.div>
        <div className="absolute inset-0 bg-white/40"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 h-full flex flex-col pt-8 pb-12">
          <motion.div
            className="hidden md:flex mb-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <Breadcrumb
              homePath={homePath}
              currentPage="About Us"
              className="-ml-4 -mt-3"
              withHoverTransition={true}
            />
          </motion.div>
          <motion.div
            className="max-w-3xl mx-auto text-center mb-auto pt-30 md:pt-0"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 drop-shadow-md">
              Your Partner in <br /> Sarawak&apos;s Digital Growth
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="bg-slate-50 py-6 w-full">
        <div className="container mx-auto px-4">
          {/* Animated Quote */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={textAnimation}
            className="max-w-7xl mx-auto mt-2 md:mt-4"
          >
            <figure className="relative">
              <Quote className="absolute -top-2 -left-2 h-10 w-10 md:h-14 md:w-14 text-slate-300" aria-hidden="true" />
              <blockquote className="relative z-10 pl-2 pt-4 md:pl-4">
                <p className="text-sm md:text-lg italic text-slate-700 leading-relaxed">
                  “Sarawak&apos;s journey toward becoming a leading digital economy by 2030 is championed by the Sarawak Digital Economy Corporation (SDEC), the agency driving state-wide policies, building infrastructure, and fostering an inclusive, innovative ecosystem. With a landscape rich in resilient SMEs, Sarawak requires more than generic solutions—it needs strategies tailored to local contexts, bridging geographical divides and modernizing traditional practices. FIT Pioneer complements this vision by serving as the practical, on-the-ground partner, equipping SMEs with the digital tools, expert guidance, and hands-on support they need to thrive. Together, SDEC lays the digital foundation while FIT Pioneer empowers businesses to confidently navigate it—ensuring that Sarawak&apos;s transformation is sustainable, homegrown, and accessible to every enterprise.”
                </p>
              </blockquote>
            </figure>
          </motion.div>
        </div>

        {/* ---------------- SDEC SECTION ---------------- */}
        <div className="relative w-full mt-12 md:mt-18">
          {/* SDEC Background Image */}
          <div className="absolute top-0 left-0 w-full h-[350px] md:h-[550px]">
            <Image src="/sdecAboutus.png" alt="SDEC Background" fill className="object-cover object-[50%_40%]" />
          </div>

          {/* SDEC Content Container */}
          <div className="relative z-10 pt-[120px] md:pt-[350px] max-w-7xl mx-auto px-4 md:px-8">

            {/* White Overlapping Card */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.7 }} variants={textAnimation}
              className="bg-white rounded-2xl border border-gray-200 shadow-[6px_6px_0_0_#d1d5db] md:shadow-[12px_12px_0_0_#d1d5db] p-6 md:p-10 w-full ml-auto"
            >
              <div className="flex justify-center mb-6">
                <a href="https://sdec.com.my/web/" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                  <Image src="/sdecLogo.png" alt="SDEC Logo" width={350} height={90} className="h-auto w-48 md:w-72" />
                </a>
              </div>
              <div className="text-slate-700 text-sm md:text-base space-y-4 italic leading-relaxed text-center md:text-left">
                <p>Sarawak Digital Economy Corporation Berhad (“SDEC”) was incorporated on 22nd January 2018, and operationalised in July 2020, as a company wholly-owned by the Sarawak Government through State Financial Secretary Inc. under the Ministry of Finance and Economic Planning.</p>
                <p>Sarawak economy must leapfrog to a digital economy otherwise it will be left far behind. Digital economy will create an entirely new dimension of development and growth for Sarawak.</p>
              </div>
            </motion.div>

            {/* Mission, Vision & Contact Wrapper */}
            <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 pb-16">

              {/* Left Column: Mission & Vision */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={textAnimation}
                className="md:border-r-[3px] border-black md:pr-10 space-y-8 flex flex-col justify-center"
              >
                <div className="text-center">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Mission</h3>
                  <ul className="text-sm md:text-base text-slate-700 space-y-2 list-disc list-inside text-left inline-block">
                    <li className="first-letter:font-bold">Synergizing our efforts with all stakeholders towards a data driven future;</li>
                    <li className="first-letter:font-bold">Developing and commercialising new ideas through translational research in digital technology;</li>
                    <li className="first-letter:font-bold">Empowering and enhancing the private sector economy to achieve the state’s GDP target; and</li>
                    <li className="first-letter:font-bold">Catalyst of an inclusive Innovation Ecosystem.</li>
                  </ul>
                </div>
                <div className="text-center">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Vision</h3>
                  <p className="text-sm md:text-base text-slate-700">To accelerate economic prosperity for all people through digitalisation.</p>
                </div>
              </motion.div>

              {/* Right Column: Contact Info */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={textAnimation}
                className="md:pl-6 flex items-center justify-center"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full max-w-lg">
                  {/* SDEC Office */}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-600 text-white mb-3">
                      <Home className="h-6 w-6" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">SDEC Corporate Office</h3>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">Sarawak Digital Economy Corporation Berhad, Level 6, Wisma Satok, Jalan Satok, 93400 Kuching, Sarawak</p>
                  </div>
                  {/* SDEC Telephone */}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-600 text-white mb-3">
                      <Phone className="h-6 w-6" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">Telephone No.</h3>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">082-549 005</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* ---------------- FIT PIONEER SECTION ---------------- */}
        <div className="relative w-full md:mt-6">
          {/* FIT Background Image */}
          <div className="absolute top-0 left-0 w-full h-[350px] md:h-[550px]">
            <Image src="/fitAboutus.png" alt="FIT Pioneer Background" fill className="object-cover object-[50%_40%]" />
          </div>

          {/* FIT Content Container */}
          <div className="relative z-10 pt-[120px] md:pt-[350px] max-w-7xl mx-auto px-4 md:px-8">

            {/* White Overlapping Card */}
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.7 }} variants={textAnimation}
              className="bg-white rounded-2xl border border-gray-200 shadow-[6px_6px_0_0_#d1d5db] md:shadow-[12px_12px_0_0_#d1d5db] p-6 md:p-10 w-full"
            >
              <div className="flex justify-center mb-6">
                <a href="https://www.fit-pioneer.com/" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                  <Image src="/fitLogo.png" alt="FIT Pioneer Logo" width={400} height={90} className="h-auto w-52 md:w-80" />
                </a>
              </div>
              <div className="text-slate-700 text-sm md:text-base space-y-4 italic leading-relaxed text-center md:text-left">
                <p>Empowering businesses through digital transformation with over 20 years of IT expertise in Asia. We deliver end-to-end consulting, implementation, and technology services focused on creating sustainable solutions.</p>
                <p>As a premier technology consulting firm, we specialize in enterprise systems integration, managed IT services, HR advisory, and business process optimization. Our team of certified professionals works across industries to solve complex business challenges through innovative technology solutions, strategic planning, and operational excellence.</p>
              </div>
            </motion.div>

            {/* Mission, Vision & Contact Wrapper */}
            <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 pb-16">

              {/* Left Column: Mission & Vision */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={textAnimation}
                className="md:border-r-[3px] border-black md:pr-10 space-y-8 flex flex-col justify-center"
              >
                <div className="text-center">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Mission</h3>
                  <p className="text-sm md:text-base text-slate-700">Empowering a smarter, sustainable future through human-centered innovation and transformative technology.</p>
                </div>
                <div className="text-center">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3">Vision</h3>
                  <p className="text-sm md:text-base text-slate-700">To accelerate progress by delivering intelligent, purpose-driven technology solutions that transform businesses, empower people, and shape a more resilient, inclusive world.</p>
                </div>
              </motion.div>

              {/* Right Column: Contact Info */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={textAnimation}
                className="md:pl-6 flex items-center justify-center"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full max-w-lg">
                  {/* FIT Office */}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-600 text-white mb-3">
                      <Home className="h-6 w-6" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">FIT Pioneer Office</h3>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">Suite 7-02, 7th Floor, Menara Keck Seng, 203, Jln Bukit Bintang, Bukit Bintang, 55100 Kuala Lumpur</p>
                  </div>
                  {/* FIT Telephone */}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-600 text-white mb-3">
                      <Phone className="h-6 w-6" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">Telephone No.</h3>
                    <p className="text-xs md:text-sm text-slate-500 mt-1">03-2116 5978</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {status === 'authenticated' ? <FooterAfter /> : <FooterBefore />}
    </>
  );
}